// material-ui
import { CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CustomTooltip from 'components/CustomTooltip';
import { getMinsAgo } from 'utils/getFormattedDate';
import React, { useCallback, useEffect, useState } from 'react';
import { IntegrationHealthMonitor, Integration, useIntegrationsQuery, useRefreshTransactionsMutation } from '../../graphql/types';
import PlatformCompanyLogo from '../../utils/platformCompanyLogo';

// assets
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

const wasRecentlyRefreshed = (integration: Integration): boolean => {
  const lastRefreshAt = integration.lastRefreshResult?.lastImportedTransactionsDate;
  if (!lastRefreshAt) return false;
  const parsed = new Date(lastRefreshAt);
  if (Number.isNaN(parsed.getTime())) return false;
  return Date.now() - parsed.getTime() < REFRESH_COOLDOWN_MS;
}

interface Props {
  open: boolean;
  handleClose: (status: boolean) => void;
}

export default function IntegrationsImportProgress({ open, handleClose }: Props) {
  const stateColors = {
    Pending: 'secondary',
    Success: 'green',
    'In progress': 'primary',
    Failed: 'error',
    Skipped: 'warning.main'
  };

  const { loading, data } = useIntegrationsQuery({
    fetchPolicy: 'cache-and-network'
  });

  const [integrations, setIntegrations] = useState<{
    [id: string]: {
      integration: Integration;
      state: 'Pending' | 'Success' | 'Failed' | 'In progress' | 'Skipped';
      elapsed: number;
      stats: IntegrationHealthMonitor | null;
    };
  }>({});
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [currentIntegration, setCurrentIntegration] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [reloadingId, setReloadingId] = useState<string | null>(null);

  const theme = useTheme();
  const [refreshTransactionsMutation] = useRefreshTransactionsMutation();

  const handleReload = useCallback(async (id: string) => {
    setReloadingId(id);
    setIntegrations((prev) => ({
      ...prev,
      [id]: { ...prev[id], state: 'In progress', elapsed: 0, stats: null }
    }));

    const start = performance.now();
    const interval = setInterval(() => {
      setIntegrations((prev) => ({
        ...prev,
        [id]: { ...prev[id], elapsed: (performance.now() - start) / 1000 }
      }));
    }, 100);

    try {
      const res = await refreshTransactionsMutation({ variables: { integrationId: id } });
      const s = res.data?.refreshTransactions;
      setIntegrations((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          state: s != null && s.lastImportedTransactionsStats.imported >= 0 ? 'Success' : 'Failed',
          elapsed: (performance.now() - start) / 1000,
          stats: s || null
        }
      }));
    } catch {
      setIntegrations((prev) => ({ ...prev, [id]: { ...prev[id], state: 'Failed' } }));
    } finally {
      clearInterval(interval);
      setReloadingId(null);
    }
  }, [refreshTransactionsMutation]);

  useEffect(() => {
    if (open && !loading && data?.integrations.data) {
      const enabledIntegrations = data.integrations.data.filter(
        (i) => i.autostart && i.settings?.enableRefresh
      ) as Integration[];
      setIntegrations(
        enabledIntegrations.reduce(
          (map, integration) => ({
            ...map,
            [integration.id]: {
              integration,
              state: wasRecentlyRefreshed(integration) ? 'Skipped' : 'Pending',
              elapsed: 0,
              stats: wasRecentlyRefreshed(integration) ? integration.lastRefreshResult || null : null
            }
          }),
          {}
        )
      );
    }
    setInProgress(open);
  }, [loading, data, open]);

  useEffect(() => {
    if (inProgress) {
      (async () => {
        for (const id of Object.keys(integrations)) {
          if (wasRecentlyRefreshed(integrations[id].integration)) {
            setIntegrations((prev) => ({
              ...prev,
              [id]: {
                ...prev[id],
                state: 'Skipped',
                elapsed: 0,
                stats: integrations[id].integration.lastRefreshResult || null
              }
            }));
            continue;
          }
          setCurrentIntegration(id);
          integrations[id].state = 'In progress';
          const start = performance.now();
          const interval = setInterval(() => {
            const elapsed = (performance.now() - start) / 1000;
            setElapsed(elapsed);
          }, 100);
          await refreshTransactionsMutation({
            variables: {
              integrationId: id
            }
          })
            .then((res) => {
              integrations[id].state = res.data?.refreshTransactions && res.data?.refreshTransactions?.lastImportedTransactionsStats.imported >= 0 ? 'Success' : 'Failed';
              integrations[id].stats = res.data?.refreshTransactions || null;
            })
            .catch(() => (integrations[id].state = 'Failed'))
            .finally(() => {
              setCurrentIntegration(null);
              setIntegrations(integrations);
            });
          clearInterval(interval);
        }
        setInProgress(false);
      })();
    }
  }, [inProgress]);

  useEffect(() => {
    if (currentIntegration) {
      integrations[currentIntegration].elapsed = elapsed;
      setIntegrations(integrations);
    }
  }, [elapsed, currentIntegration]);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      sx={{ '& .MuiDialog-paper': { minWidth: 650 } }}
      onClose={(event, reason) => {
        if (inProgress && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        handleClose(false);
      }}
      keepMounted
      aria-labelledby="item-warning-title"
      aria-describedby="item-warning-description"
    >
      <DialogTitle>Refreshing Transactions</DialogTitle>
      {open && (
        <DialogContent>
          <TableContainer sx={{ mt: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableBody>
                {Object.entries(integrations).map(([id, { integration, state, elapsed, stats }]) => (
                  <TableRow key={id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <PlatformCompanyLogo integration={integration} mt={0} />
                    </TableCell>
                    <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                      {integration.name}
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 120, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <CustomTooltip title={state === 'Skipped' ? 'Reloaded less than 5 minutes ago.' : ''} disableHoverListener={state !== 'Skipped'}>
                        <Typography color={stateColors[state]} component="span">
                          {state}
                        </Typography>
                      </CustomTooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'nowrap' }}>
                        <Box component="span" sx={{ flexShrink: 0 }}>
                          {state === 'Skipped' && stats?.lastImportedTransactionsDate
                          ? getMinsAgo(stats.lastImportedTransactionsDate)
                          : `${elapsed === 0 ? '-.-' : elapsed.toFixed(1)} s`}
                        </Box>
                        {(state === 'Success' || state === 'Skipped') && stats && (
                          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                            Imported: {stats.lastImportedTransactionsStats?.imported} | New: {stats.lastImportedTransactionsStats?.inserted} | Other:{' '}
                            {(stats.lastImportedTransactionsStats?.updated || 0) + (stats.lastImportedTransactionsStats?.deleted || 0)}
                          </Box>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 48, verticalAlign: 'middle' }}>
                      {(state === 'Failed' || state === 'Skipped') && (
                        <CustomTooltip title="Reload">
                          <span>
                            <IconButton
                              size="small"
                              disabled={reloadingId === id}
                              sx={{ color: theme.palette.lightgreen?.main }}
                              onClick={() => handleReload(id)}
                              aria-label="Reload processor transactions"
                            >
                              <ReloadOutlined />
                            </IconButton>
                          </span>
                        </CustomTooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      )}
      <DialogActions>
        <Button color="secondary" disabled={inProgress} size="small" onClick={() => handleClose(true)}startIcon={<CloseOutlined />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}